import { useState, useEffect } from "react";
import {
  Container,
  createTheme,
  ThemeProvider,
  Fab,
  Box,
  Fade,
  Zoom,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import HeaderLandingComponent from "./components/header.component";
import MainFrameComponent from "./components/main.frame.component";
import FeaturesShowcaseComponent from "./components/features.showcase.component";
import BenefitsComponent from "./components/benefits.frame.component";
import ContactPage from "./components/contact.frame.component";
import AboutPage from "./components/about.frame.component";

export default function LandingPage() {
  const theme = createTheme({
    cssVariables: true,
    palette: {
      primary: {
        main: "#FF7323",
        light: "#FF9B5A",
        dark: "#D85A00",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#1A73E8",
        light: "#4285F4",
        dark: "#1557B0",
        contrastText: "#FFFFFF",
      },
      tertiary: {
        main: "#25D366",
        light: "#4AE074",
        dark: "#1BAF4F",
        contrastText: "#FFFFFF",
      },
      background: {
        default: "#FAFBFC",
        paper: "#FFFFFF",
        gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        accent: "#F8F9FA",
      },
      text: {
        primary: "#1A1A1A",
        secondary: "#4A5568",
        tertiary: "#718096",
      },
      grey: {
        50: "#F7FAFC",
        100: "#EDF2F7",
        200: "#E2E8F0",
        300: "#CBD5E0",
        400: "#A0AEC0",
        500: "#718096",
        600: "#4A5568",
        700: "#2D3748",
        800: "#1A202C",
        900: "#171923",
      },
      success: {
        main: "#38A169",
        light: "#48BB78",
        dark: "#2F855A",
      },
      error: {
        main: "#E53E3E",
        light: "#FC8181",
        dark: "#C53030",
      },
      warning: {
        main: "#D69E2E",
        light: "#F6E05E",
        dark: "#B7791F",
      },
      info: {
        main: "#3182CE",
        light: "#63B3ED",
        dark: "#2C5282",
      },
    },
    typography: {
      fontFamily:
        '"Inter", "SF Pro Display", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: {
        fontSize: "3.75rem",
        fontWeight: 800,
        lineHeight: 1.1,
        letterSpacing: "-0.025em",
        background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      },
      h2: {
        fontSize: "3rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
      },
      h3: {
        fontSize: "2.25rem",
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
      },
      h4: {
        fontSize: "1.875rem",
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: "1.5rem",
        fontWeight: 500,
        lineHeight: 1.4,
      },
      h6: {
        fontSize: "1.25rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: "1.125rem",
        lineHeight: 1.7,
        color: "#4A5568",
        fontWeight: 400,
      },
      body2: {
        fontSize: "1rem",
        lineHeight: 1.6,
        color: "#718096",
        fontWeight: 400,
      },
      subtitle1: {
        fontSize: "1.125rem",
        fontWeight: 500,
        lineHeight: 1.6,
      },
      subtitle2: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.5,
      },
      button: {
        textTransform: "none",
        fontWeight: 600,
        fontSize: "1rem",
        letterSpacing: "0.02em",
      },
      caption: {
        fontSize: "0.875rem",
        lineHeight: 1.4,
        color: "#718096",
      },
    },
    shape: {
      borderRadius: 16,
    },
    spacing: 8,
    shadows: [
      "none",
      "0px 1px 2px rgba(0, 0, 0, 0.05)",
      "0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)",
      "0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)",
      "0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)",
      "0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)",
      "0px 25px 50px rgba(0, 0, 0, 0.15), 0px 12px 18px rgba(0, 0, 0, 0.08)",
      // Additional custom shadows
      "0px 35px 60px rgba(0, 0, 0, 0.2), 0px 15px 25px rgba(0, 0, 0, 0.1)",
    ],
    breakpoints: {
      values: {
        xs: 0,
        sm: 640,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1536,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "14px 28px",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
            },
            "&:active": {
              transform: "translateY(0px)",
              transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1)",
            },
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
            color: "#FFFFFF",
            "&:hover": {
              background: "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
              boxShadow: "0px 15px 35px rgba(255, 115, 35, 0.4)",
            },
          },
          containedSecondary: {
            background: "linear-gradient(135deg, #1A73E8 0%, #1557B0 100%)",
            color: "#FFFFFF",
            "&:hover": {
              background: "linear-gradient(135deg, #1557B0 0%, #0F3F7C 100%)",
              boxShadow: "0px 15px 35px rgba(26, 115, 232, 0.4)",
            },
          },
          outlined: {
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
              backgroundColor: "rgba(255, 115, 35, 0.04)",
            },
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.15)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              boxShadow: "0px 15px 35px rgba(0, 0, 0, 0.2)",
              transform: "translateY(-4px) scale(1.05)",
            },
            "&:active": {
              transform: "translateY(-2px) scale(0.98)",
            },
          },
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: "24px",
            paddingRight: "24px",
            "@media (min-width: 640px)": {
              paddingLeft: "32px",
              paddingRight: "32px",
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          h1: {
            background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          },
        },
      },
    },
  });

  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Função para monitorar a rolagem e calcular progresso
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = (scrollTop / docHeight) * 100;

    setShowScrollButton(scrollTop > 400);
    setScrollProgress(scrollPercent);
  };

  // Adiciona evento de rolagem
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Função para rolar até o topo com efeito suave
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.default,
          minWidth: "100%",
          minHeight: "100vh",
          margin: 0,
          padding: 0,
          position: "relative",
          overflow: "hidden",
          "& > *": {
            scrollMarginTop: "100px",
          },
        }}
        maxWidth={false}
        disableGutters
      >
        {/* Progress indicator */}
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: `${scrollProgress}%`,
            height: 4,
            background:
              "linear-gradient(90deg, #FF7323 0%, #1A73E8 50%, #25D366 100%)",
            zIndex: 9999,
            transition: "width 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
            borderRadius: "0 0 2px 0",
            boxShadow: "0 2px 8px rgba(255, 115, 35, 0.3)",
          }}
        />

        {/* Sections */}
        <HeaderLandingComponent theme={theme} />

        <Box component="main" sx={{ position: "relative" }}>
          <section id="mainFrame">
            <MainFrameComponent theme={theme} />
          </section>
          <section id="features">
            <FeaturesShowcaseComponent theme={theme} />
          </section>
          <section id="benefits">
            <BenefitsComponent theme={theme} />
          </section>
          <section id="about">
            <AboutPage theme={theme} />
          </section>

          <section id="contact">
            <ContactPage theme={theme} />
          </section>
        </Box>

        {/* Floating Action Buttons modernizados */}
        <Box
          sx={{
            position: "fixed",
            bottom: { xs: 24, sm: 32 },
            right: { xs: 24, sm: 32 },
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1300,
          }}
        >
          {/* Scroll to top button */}
          <Zoom in={showScrollButton} timeout={400}>
            <Fab
              color="primary"
              aria-label="voltar ao topo"
              onClick={scrollToTop}
              size="medium"
              sx={{
                background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                color: "#FFFFFF",
                width: 56,
                height: 56,
                boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.35)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                  transform: "translateY(-4px) scale(1.1)",
                  boxShadow: "0px 15px 35px rgba(255, 115, 35, 0.45)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.02)",
                  transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1)",
                },
              }}
            >
              <ArrowUpwardIcon sx={{ fontSize: 22 }} />
            </Fab>
          </Zoom>

          {/* WhatsApp button */}
          <Fade in={true} timeout={600}>
            <Fab
              aria-label="WhatsApp"
              href="https://wa.me/5551997971635?text=Quero%20saber%20sobre%20o%20wala%20smart%20work"
              target="_blank"
              rel="noopener noreferrer"
              size="large"
              sx={{
                background: "linear-gradient(135deg, #25D366 0%, #1BAF4F 100%)",
                color: "#FFFFFF",
                width: 64,
                height: 64,
                position: "relative",
                boxShadow: "0px 10px 30px rgba(37, 211, 102, 0.35)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1BAF4F 0%, #128C3D 100%)",
                  transform: "translateY(-4px) scale(1.1)",
                  boxShadow: "0px 15px 35px rgba(37, 211, 102, 0.45)",
                },
                "&:active": {
                  transform: "translateY(-2px) scale(1.02)",
                  transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1)",
                },
                // Efeito de pulso otimizado
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -4,
                  left: -4,
                  right: -4,
                  bottom: -4,
                  background:
                    "linear-gradient(135deg, #25D366 0%, #1BAF4F 100%)",
                  borderRadius: "50%",
                  zIndex: -1,
                  opacity: 0,
                  animation: "whatsappPulse 3s ease-in-out infinite",
                },
                "@keyframes whatsappPulse": {
                  "0%, 100%": {
                    opacity: 0,
                    transform: "scale(1)",
                  },
                  "50%": {
                    opacity: 0.2,
                    transform: "scale(1.15)",
                  },
                },
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 30 }} />
            </Fab>
          </Fade>
        </Box>

        {/* Background decorative elements */}
        <Box
          sx={{
            position: "fixed",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background:
              "radial-gradient(circle, rgba(255, 115, 35, 0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: -1,
            animation: "floatSlow 8s ease-in-out infinite",
            "@keyframes floatSlow": {
              "0%, 100%": {
                transform: "translateY(0px) translateX(0px) rotate(0deg)",
                opacity: 0.6,
              },
              "50%": {
                transform: "translateY(-25px) translateX(15px) rotate(180deg)",
                opacity: 0.8,
              },
            },
          }}
        />

        <Box
          sx={{
            position: "fixed",
            bottom: -150,
            left: -150,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(26, 115, 232, 0.08) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: -1,
            animation: "floatReverse 10s ease-in-out infinite reverse",
            "@keyframes floatReverse": {
              "0%, 100%": {
                transform: "translateY(0px) translateX(0px) rotate(0deg)",
                opacity: 0.5,
              },
              "50%": {
                transform: "translateY(30px) translateX(-20px) rotate(-180deg)",
                opacity: 0.7,
              },
            },
          }}
        />

        {/* Additional subtle element */}
        <Box
          sx={{
            position: "fixed",
            top: "40%",
            left: -100,
            width: 200,
            height: 200,
            background:
              "radial-gradient(circle, rgba(37, 211, 102, 0.04) 0%, transparent 60%)",
            borderRadius: "50%",
            zIndex: -1,
            animation: "gentleFloat 12s ease-in-out infinite",
            "@keyframes gentleFloat": {
              "0%, 100%": {
                transform: "translateY(0px) scale(1)",
                opacity: 0.3,
              },
              "50%": {
                transform: "translateY(-15px) scale(1.1)",
                opacity: 0.5,
              },
            },
          }}
        />
      </Container>
    </ThemeProvider>
  );
}
